import React, { useState, useEffect } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import axios from "axios";
import { baseurl, imgurl } from "../../baseurl";
import { FaEye, FaTrash, FaBan, FaSearch, FaUsers, FaUserCheck, FaUserSlash, FaCalendarAlt, FaEnvelope, FaPhone } from "react-icons/fa";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";

function AllCustomers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    fromDate: "",
    toDate: "",
  });

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseurl}getAllCustomers`);
      if (res.data.success) setCustomers(res.data.customers || []);
    } catch (err) {
      Swal.fire("Error", "Failed to fetch customers", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const deleteCustomer = async (id) => {
    const confirm = await Swal.fire({
      title: "Confirm Deletion?",
      text: "This will permanently remove the customer profile and history.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      confirmButtonText: "Delete Permanently",
    });

    if (confirm.isConfirmed) {
      try {
        await axios.delete(`${baseurl}deleteCustomer/${id}`);
        Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Record Deleted", showConfirmButton: false, timer: 1500 });
        fetchCustomers();
      } catch (err) {
        Swal.fire("Error", "Could not remove record", "error");
      }
    }
  };

  const toggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    try {
      await axios.put(`${baseurl}updateCustomerStatus/${id}`, { status: newStatus });
      Swal.fire({ toast: true, position: "top-end", icon: "success", title: `Account ${newStatus}`, showConfirmButton: false, timer: 1500 });
      fetchCustomers();
    } catch (err) {
      Swal.fire("Error", "Status update failed", "error");
    }
  };

  const filteredCustomers = customers.filter((c) => {
    const searchLower = filters.search.toLowerCase();
    const matchesSearch = c.name?.toLowerCase().includes(searchLower) || c.email?.toLowerCase().includes(searchLower) || c.phone?.toLowerCase().includes(searchLower);
    const matchesStatus = filters.status ? c.status === filters.status : true;
    const matchesFromDate = filters.fromDate ? new Date(c.created_at) >= new Date(filters.fromDate) : true;
    const matchesToDate = filters.toDate ? new Date(c.created_at) <= new Date(filters.toDate) : true;
    return matchesSearch && matchesStatus && matchesFromDate && matchesToDate;
  });

  return (
    <>
      <Topbar />
      <Sidebar />

      <main className="main-wrapper bg-light">
        <div className="main-content p-3 p-lg-4">
          
          {/* Compact Header */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h5 className="fw-bold mb-0 text-dark">Customer Database</h5>
              <p className="text-muted extra-small mb-0">Manage user permissions, verify identities, and monitor registration trends.</p>
            </div>
            <div className="d-flex gap-2">
               <div className="bg-white px-3 py-1 rounded-pill shadow-sm border small">
                  <span className="text-muted">Total: </span><span className="fw-bold">{customers.length}</span>
               </div>
               <div className="bg-white px-3 py-1 rounded-pill shadow-sm border small">
                  <span className="text-muted">Active: </span><span className="fw-bold text-success">{customers.filter(c=>c.status==='active').length}</span>
               </div>
            </div>
          </div>

          {/* Ultra-Compact Filter Bar */}
          <div className="card border-0 shadow-sm rounded-3 mb-3">
            <div className="card-body py-2 px-3">
              <div className="row g-2 align-items-center">
                <div className="col-md-4">
                  <div className="input-group input-group-sm">
                    <span className="input-group-text bg-transparent border-end-0 text-muted"><FaSearch size={12}/></span>
                    <input 
                      type="text" 
                      className="form-control form-control-sm border-start-0 ps-0" 
                      placeholder="Search name, email, or mobile..." 
                      value={filters.search}
                      onChange={(e) => setFilters({...filters, search: e.target.value})}
                    />
                  </div>
                </div>
                <div className="col-md-2">
                  <select className="form-select form-select-sm" value={filters.status} onChange={(e) => setFilters({...filters, status: e.target.value})}>
                    <option value="">Status: All</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div className="col-md-4">
                  <div className="d-flex gap-1 align-items-center">
                    <Flatpickr
                      options={{ dateFormat: "Y-m-d" }}
                      className="form-control form-control-sm text-center"
                      placeholder="Joined From"
                      onChange={(date) => setFilters({ ...filters, fromDate: date[0] })}
                    />
                    <span className="text-muted small">to</span>
                    <Flatpickr
                      options={{ dateFormat: "Y-m-d" }}
                      className="form-control form-control-sm text-center"
                      placeholder="Joined To"
                      onChange={(date) => setFilters({ ...filters, toDate: date[0] })}
                    />
                  </div>
                </div>
                <div className="col-md-2 text-end">
                   <button className="btn btn-sm btn-link text-decoration-none text-muted p-0 extra-small fw-bold" onClick={() => setFilters({search: "", status: "", fromDate: "", toDate: ""})}>RESET</button>
                </div>
              </div>
            </div>
          </div>

          {/* High Density Table */}
          <div className="card border-0 shadow-sm rounded-3 overflow-hidden">
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0 custom-table">
                <thead className="bg-white border-bottom text-uppercase">
                  <tr>
                    <th className="ps-3 py-2">Account</th>
                    <th>Communication</th>
                    <th>Lifecycle</th>
                    <th>Status</th>
                    <th className="text-end pe-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="5" className="text-center py-4"><div className="spinner-border spinner-border-sm text-primary"></div></td></tr>
                  ) : filteredCustomers.map((c) => (
                    <tr key={c.id}>
                      <td className="ps-3 py-2">
                        <div className="d-flex align-items-center">
                          {c.profile_image ? (
                            <img src={`${imgurl}${c.profile_image}`} className="rounded-circle me-2 border shadow-sm" style={{ width: 32, height: 32, objectFit: "cover" }} />
                          ) : (
                            <div className="rounded-circle me-2 bg-soft-primary text-primary d-flex align-items-center justify-content-center fw-bold shadow-sm" style={{ width: 32, height: 32, fontSize: '12px' }}>
                              {c.name?.charAt(0)}
                            </div>
                          )}
                          <div style={{lineHeight: '1.1'}}>
                            <div className="fw-bold text-dark small">{c.name}</div>
                            <span className="text-muted italic" style={{fontSize: '9px'}}>Ref: {c.uuid?.substring(0, 10)}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                         <div className="d-flex flex-column" style={{fontSize: '11px'}}>
                            <span className="text-dark"><FaEnvelope className="me-1 text-muted" size={10}/> {c.email}</span>
                            <span className="text-muted"><FaPhone className="me-1 text-muted" size={10}/> {c.phone || "N/A"}</span>
                         </div>
                      </td>
                      <td>
                        <div className="small text-muted">
                           <FaCalendarAlt size={10} className="me-1"/> {new Date(c.created_at).toLocaleDateString("en-GB")}
                        </div>
                      </td>
                      <td>
                        <span className={`status-pill ${c.status === "active" ? "active" : "inactive"}`}>
                          {c.status}
                        </span>
                      </td>
                      <td className="text-end pe-3">
                        <div className="btn-group">
                          <Link to={`/customer-view/${c.uuid}`} className="btn btn-xs btn-outline-light border text-primary" title="Details"><FaEye size={12}/></Link>
                          <button onClick={() => toggleStatus(c.id, c.status)} className={`btn btn-xs border ${c.status === 'active' ? 'btn-outline-light text-warning' : 'btn-warning text-white'}`} title="Restrict"><FaBan size={12}/></button>
                          <button onClick={() => deleteCustomer(c.id)} className="btn btn-xs btn-outline-light border text-danger" title="Purge"><FaTrash size={12}/></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredCustomers.length === 0 && !loading && (
              <div className="text-center py-4 bg-white">
                <p className="text-muted small mb-0">No records found for the selected criteria.</p>
              </div>
            )}
          </div>
        </div>
      </main>

      <style>{`
        .extra-small { font-size: 11px; }
        .italic { font-style: italic; }
        .bg-soft-primary { background-color: #eef2ff; }
        
        .custom-table thead th { font-size: 10px; color: #64748b; letter-spacing: 0.05em; border-bottom: 1px solid #f1f5f9; }
        .custom-table tbody td { border-bottom: 1px solid #f8fafc; }
        
        .status-pill { padding: 2px 8px; border-radius: 4px; font-size: 10px; font-weight: 700; text-transform: uppercase; }
        .status-pill.active { background: #dcfce7; color: #166534; }
        .status-pill.inactive { background: #fee2e2; color: #991b1b; }

        .btn-xs { padding: 0.25rem 0.5rem; font-size: 0.75rem; border-radius: 4px; }
        .btn-outline-light { background: #fff; color: #64748b; border-color: #e2e8f0; }
        .btn-outline-light:hover { background: #f8fafc; border-color: #cbd5e1; }

        .form-control-sm, .form-select-sm { font-size: 12px; }
        .input-group-text { border-color: #dee2e6; }
      `}</style>
    </>
  );
}

export default AllCustomers;